import * as React from 'react'

import { ListChecks, X } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import {
  IFormMaterialResource,
  IPermitTypeField,
  ISiteLocation,
  LocationType,
  PermitFieldType,
} from '~/client/graph'
import { Button } from '~/client/src/shared/components/Button/Button'
import {
  LINK_UNDERLINE_ON_GROUP_HOVER,
  ObjectReference,
} from '~/client/src/shared/components/ObjectReference/ObjectReference'
import { StackedReference } from '~/client/src/shared/components/ObjectReference/StackedReference'
import PermitFieldIcon from '~/client/src/shared/components/PermitFieldIcon/PermitFieldIcon'
import {
  getResourceChipLabel,
  IResourceChipItem,
} from '~/client/src/shared/components/ResourceChipList'
import { ResourceValueReference } from '~/client/src/shared/components/ResourceLink/ResourceValueReference'
import StruxhubResourceSelector from '~/client/src/shared/components/StruxhubInputs/StruxhubSelector/StruxhubResourceSelector'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import useMaterialResources from '~/client/src/shared/hooks/useMaterialResources'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import { renderResourceSemanticIcon } from '~/client/src/shared/resources/icons/resourceSemanticIcons'
import InitialState from '~/client/src/shared/stores/InitialState'
import { getResourcePlaceholderText } from '~/client/src/shared/utils/getResourcePlaceholderText'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'
import HoursOfOperationNote, {
  renderResourceNote,
} from '../HoursOfOperationNote'
import PermitBaseFormField from './PermitBaseFormField'

interface IProps {
  typeField: IPermitTypeField
  isViewMode: boolean

  store: SitePermitCreationFormStore
  fieldsStore: IPermitFieldsStore

  parentInstanceId?: string
  parentValueIndex?: number

  state?: InitialState
}

const renderMaterialChipIcon = () =>
  renderResourceSemanticIcon(
    'material',
    'tw:block tw:size-4 tw:text-muted-foreground tw:ml-0.5 tw:mr-1',
  )

const renderMaterialLabel = (record?: IResourceRecord): string => {
  if (!record) return null
  return record.stxResourceId
    ? `${record.stxResourceId}: ${record.name}`
    : record.name
}

// A single material reads like a single location: its glyph and name, its ID beneath.
const renderMaterialReference = (record: IResourceRecord): JSX.Element => (
  <StackedReference
    size="dense"
    secondLine={record.stxResourceId}
    reference={
      <ObjectReference
        icon={renderResourceSemanticIcon('material')}
        variant="plain"
        size="dense"
      >
        <span title={record.name} className={LINK_UNDERLINE_ON_GROUP_HOVER}>
          {record.name}
        </span>
      </ObjectReference>
    }
  />
)

function PermitFormMaterialResourceField({
  typeField,
  isViewMode,
  store,
  fieldsStore,
  parentInstanceId,
  parentValueIndex,
  state,
}: IProps) {
  const projectId = state?.activeProject?.id
  // Deleted Materials included: a workflow keeps showing what it was saved with.
  const { recordsWithDeletedItems, isReady } = useMaterialResources({
    projectId,
  })

  const materialsById = React.useMemo(() => {
    const map = new Map<string, IResourceRecord>()
    for (const r of recordsWithDeletedItems) {
      map.set(String(r.id), r)
    }
    return map
  }, [recordsWithDeletedItems])

  // Computed inline (not memoised) so the observer wrapper retracks the
  // store's observable read on every render — otherwise picking a value
  // would not trigger a re-render.
  const storedValues = fieldsStore.getFieldValues<IFormMaterialResource>(
    typeField.id,
    undefined,
    undefined,
    false,
    parentInstanceId,
    parentValueIndex,
  )
  const fieldValues: IFormMaterialResource[] = storedValues?.length
    ? storedValues
    : [{ locationId: null }]

  const handlePickerChange = React.useCallback(
    <T,>(value: T) => {
      const locations = (Array.isArray(value) ? value : [value]) as Array<
        ISiteLocation | null | undefined
      >

      const newValues: IFormMaterialResource[] = locations.reduce<
        IFormMaterialResource[]
      >((acc, loc) => {
        if (loc?.id) {
          acc.push({ locationId: loc.id })
        }
        return acc
      }, [])

      if (typeField.isMultiple) {
        fieldsStore.changeMultipleFieldValues(
          typeField,
          newValues,
          parentInstanceId,
          parentValueIndex,
        )
        return
      }

      fieldsStore.changeFieldValue(
        typeField,
        0,
        newValues[0] || null,
        parentInstanceId,
        parentValueIndex,
      )
    },
    [fieldsStore, typeField, parentInstanceId, parentValueIndex],
  )

  const openPicker = React.useCallback(() => {
    store.setSelectedField(
      typeField,
      0,
      undefined,
      undefined,
      parentInstanceId,
      parentValueIndex,
    )
    store.setSelectedLocationType(LocationType.Material)
    store.setSelectedOnChangeHandler(handlePickerChange)
  }, [store, typeField, parentInstanceId, parentValueIndex, handlePickerChange])

  // A copy with isMultiple forced to false so the picker opens in
  // single-select mode for individual selectors and "+ Add Material".
  const singleSelectTypeField = React.useMemo(
    () => ({ ...typeField, isMultiple: false }),
    [typeField],
  )

  // Opens the picker in single-select mode to change one material value.
  const openPickerForIndex = React.useCallback(
    (index: number) => {
      const handleChange = <T,>(value: T) => {
        const locations = (Array.isArray(value) ? value : [value]) as Array<
          ISiteLocation | null | undefined
        >
        const newValue = locations[0]?.id
          ? { locationId: locations[0].id }
          : null
        fieldsStore.changeFieldValue(
          typeField,
          index,
          newValue,
          parentInstanceId,
          parentValueIndex,
        )
      }
      store.setSelectedField(
        singleSelectTypeField,
        index,
        undefined,
        undefined,
        parentInstanceId,
        parentValueIndex,
      )
      store.setSelectedLocationType(LocationType.Material)
      store.setSelectedOnChangeHandler(handleChange)
    },
    [
      store,
      fieldsStore,
      typeField,
      singleSelectTypeField,
      parentInstanceId,
      parentValueIndex,
    ],
  )

  const removeValue = React.useCallback(
    (index: number) => {
      fieldsStore.removeFieldValue(
        typeField.id,
        index,
        parentInstanceId,
        parentValueIndex,
      )
      // Removal bypasses the picker path; re-run the Save-gate validation.
      store.scheduleLocationsValidation()
    },
    [store, fieldsStore, typeField.id, parentInstanceId, parentValueIndex],
  )

  const removeMaterialResourceById = React.useCallback(
    (id: string) => {
      store.removePendingResource(id)
      // Re-validate so the Save gate updates after removing the value.
      store.scheduleLocationsValidation()

      const rawValues = fieldsStore.getFieldValues<
        IFormMaterialResource | IFormMaterialResource[]
      >(
        typeField.id,
        undefined,
        undefined,
        false,
        parentInstanceId,
        parentValueIndex,
      )

      const firstValue = rawValues?.[0]
      if (Array.isArray(firstValue)) {
        const updated = firstValue.filter(v => v?.locationId !== id)
        const newValue = updated.length > 0 ? updated : null
        fieldsStore.changeFieldValue(
          typeField,
          0,
          newValue,
          parentInstanceId,
          parentValueIndex,
        )
        return
      }

      const idx = (rawValues || []).findIndex(
        v => !Array.isArray(v) && v?.locationId === id,
      )
      if (idx >= 0) {
        removeValue(idx)
      }
    },
    [
      store,
      fieldsStore,
      typeField,
      parentInstanceId,
      parentValueIndex,
      removeValue,
    ],
  )

  // Computed inline (not memoised) so the observer wrapper retracks the
  // store's observable read on every render — same pattern as storedValues.
  const hasAssociatedFields =
    fieldsStore.getAvailableConditionalFields(
      typeField,
      0,
      parentInstanceId,
      parentValueIndex,
    )?.length > 0

  // Eagerly create the field entry so that conditionalParentInstanceId
  // (the instanceId used as parentInstanceId for associated fields) is
  // stable from mount.  Without this, associated fields rendered before
  // a material is picked write data keyed against an undefined parent;
  // once a material IS picked the parent resolves to a real instanceId
  // and the previously written data becomes unreachable.
  React.useEffect(() => {
    if (!hasAssociatedFields || isViewMode) return
    const existing = fieldsStore.editableEntity?.getField(
      typeField.id,
      parentInstanceId,
      parentValueIndex,
    )
    if (!existing) {
      fieldsStore.changeFieldValue(
        typeField,
        0,
        null,
        parentInstanceId,
        parentValueIndex,
      )
    }
  }, [
    hasAssociatedFields,
    isViewMode,
    fieldsStore,
    typeField,
    parentInstanceId,
    parentValueIndex,
  ])

  // Only show per-material group titles when 2+ materials are actually
  // selected — counted from truthy locationIds so the placeholder
  // `{ locationId: null }` row never triggers a title.
  const realValueCount = fieldValues.reduce(
    (n, v) => (v?.locationId ? n + 1 : n),
    0,
  )
  const showGroupTitle = hasAssociatedFields && realValueCount > 1

  const toChipItem = React.useCallback(
    (locationId: string): IResourceChipItem => {
      const pending = store.pendingResources.get(locationId)
      if (pending) return { id: locationId, label: pending.name }

      const record = materialsById.get(String(locationId))
      if (record) {
        return {
          id: locationId,
          label: renderMaterialLabel(record),
          isDeleted: record.isDeleted,
        }
      }
      // Unresolvable once the store settled — the Material is gone for good.
      // Until then the id stands in rather than mislabelling a loading value.
      return isReady
        ? { id: locationId, isDeleted: true }
        : { id: locationId, label: locationId }
    },
    [store, materialsById, isReady],
  )

  // Only a saved, live material has a reference to show; anything else keeps its chip.
  const getMaterialReference = React.useCallback(
    (locationId: string): JSX.Element | null => {
      if (store.pendingResources.has(locationId)) return null
      const record = materialsById.get(String(locationId))
      return record && !record.isDeleted
        ? renderMaterialReference(record)
        : null
    },
    [store, materialsById],
  )

  const renderGroupTitle = React.useCallback(
    (
      value: IFormMaterialResource,
      displayIndex: number,
      onRemove?: (locationId: string) => void,
    ): JSX.Element | null => {
      if (!value?.locationId) return null
      const name = getResourceChipLabel(toChipItem(value.locationId))
      return (
        <div className="tw:flex tw:items-center tw:gap-2 tw:py-2 tw:mt-3 tw:border-b tw:border-border">
          <PermitFieldIcon fieldName={PermitFieldType.MaterialResource} />
          <span className="tw:text-body-md tw:font-semibold tw:text-foreground tw:flex-1 tw:line-clamp-2 tw:break-words">
            {displayIndex}. {name}
          </span>
          {onRemove && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onRemove(value.locationId)}
              aria-label={Localization.translator.remove}
            >
              <X className="tw:size-4" />
            </Button>
          )}
        </div>
      )
    },
    [toChipItem],
  )

  // When hasAssociatedFields + isMultiple, each value gets its own
  // individual selector (matching the Figma). Otherwise, a single
  // chip-based selector at index 0 shows all values.
  const useIndividualSelectors = hasAssociatedFields && typeField.isMultiple

  const renderComponent = React.useCallback(
    (value: IFormMaterialResource, index: number): JSX.Element => {
      const {
        caption,
        isMandatory,
        allowSelectExisting = true,
        allowCreateNew = true,
      } = typeField

      const placeholderText = getResourcePlaceholderText(
        allowSelectExisting,
        allowCreateNew,
      )

      // Individual selector per value — each material gets its own row.
      if (useIndividualSelectors) {
        const chipItem: IResourceChipItem[] = value?.locationId
          ? [toChipItem(value.locationId)]
          : []

        return (
          <StruxhubResourceSelector
            className="tw:overflow-hidden"
            label={caption}
            isRequired={isMandatory}
            isTopMarginForced
            items={chipItem}
            placeholderText={placeholderText}
            allowSelectExisting={allowSelectExisting}
            onClick={() => openPickerForIndex(index)}
            onSelectExisting={() => {
              store.setFieldSelectionMode('selectExisting')
              openPickerForIndex(index)
            }}
            renderIcon={renderMaterialChipIcon}
            valueContent={
              value?.locationId && getMaterialReference(value.locationId)
            }
            isSingleValue
            note={renderResourceNote(store, [value?.locationId])}
          />
        )
      }

      // Chip-based selector — one selector at index 0 shows all values.
      const displayIndex =
        fieldValues
          .slice(0, index)
          .reduce((n, v) => (v?.locationId ? n + 1 : n), 0) + 1
      if (index > 0) {
        return showGroupTitle
          ? renderGroupTitle(value, displayIndex, removeMaterialResourceById)
          : null
      }

      const flatValues = fieldValues.filter(v => !!v?.locationId)
      const chipItems: IResourceChipItem[] = flatValues.map(v =>
        toChipItem(v.locationId),
      )

      return (
        <>
          <StruxhubResourceSelector
            className="tw:overflow-hidden"
            label={caption}
            isRequired={isMandatory}
            isTopMarginForced
            items={chipItems}
            placeholderText={placeholderText}
            allowSelectExisting={allowSelectExisting}
            onClick={openPicker}
            onSelectExisting={() => {
              store.setFieldSelectionMode('selectExisting')
              openPicker()
            }}
            onRemove={
              typeField.isMultiple ? removeMaterialResourceById : undefined
            }
            renderIcon={renderMaterialChipIcon}
            valueContent={
              !typeField.isMultiple &&
              flatValues.length === 1 &&
              getMaterialReference(flatValues[0].locationId)
            }
            note={renderResourceNote(
              store,
              flatValues.map(v => v.locationId),
              id => getResourceChipLabel(toChipItem(id)),
            )}
          />
          {showGroupTitle &&
            renderGroupTitle(value, displayIndex, removeMaterialResourceById)}
        </>
      )
    },
    [
      typeField,
      fieldValues,
      toChipItem,
      getMaterialReference,
      store,
      openPicker,
      openPickerForIndex,
      removeMaterialResourceById,
      showGroupTitle,
      renderGroupTitle,
      useIndividualSelectors,
    ],
  )

  const renderViewMode = React.useCallback(
    (value: IFormMaterialResource): JSX.Element => {
      if (!value?.locationId) {
        return <div className="text large word-break-all">{NO_VALUE}</div>
      }
      // Deleted Materials stay on the workflow, marked, exactly as in edit mode.
      const label = getResourceChipLabel(toChipItem(value.locationId))

      if (showGroupTitle) {
        const displayIndex =
          fieldValues
            .filter(v => !!v?.locationId)
            .findIndex(v => v?.locationId === value?.locationId) + 1
        return (
          <>
            {renderGroupTitle(value, displayIndex || 1)}
            <HoursOfOperationNote
              store={store}
              locationIds={[value.locationId]}
              isViewMode
            />
          </>
        )
      }

      const materialReference = getMaterialReference(value.locationId)

      return (
        <div className="tw:flex tw:flex-col tw:items-start tw:min-w-0">
          {materialReference ? (
            <ResourceValueReference
              resourceTypeId={ResourceTypeId.MATERIALS}
              recordId={value.locationId}
              projectId={projectId}
              onOpen={() =>
                state?.setResourceDisplayedOnGlobalCard(
                  value.locationId,
                  ResourceTypeId.MATERIALS,
                )
              }
            >
              {materialReference}
            </ResourceValueReference>
          ) : (
            <span className="tw:text-app-dense-medium tw:text-foreground tw:break-words">
              {label}
            </span>
          )}
          <HoursOfOperationNote
            store={store}
            locationIds={[value.locationId]}
            isViewMode
            className="tw:ml-6"
          />
        </div>
      )
    },
    [
      fieldValues,
      toChipItem,
      getMaterialReference,
      showGroupTitle,
      renderGroupTitle,
      store,
      state,
      projectId,
    ],
  )

  const handleBulkClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      store.setFieldSelectionMode('selectExisting')
      openPicker()
    },
    [store, openPicker],
  )

  const renderTitle = React.useCallback(
    (_value: IFormMaterialResource, index: number): JSX.Element | null => {
      const displayIndex = index + 1
      return (
        <div
          className={`tw:flex tw:items-center tw:gap-2 tw:py-2 tw:px-4${
            index > 0 ? ' tw:border-t tw:border-border' : ''
          }`}
        >
          <span className="tw:text-body-md tw:font-semibold tw:text-foreground tw:flex-1 tw:line-clamp-2 tw:break-words">
            {Localization.translator.item} #{displayIndex}
          </span>
          {!isViewMode && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => removeValue(index)}
              aria-label={Localization.translator.remove}
            >
              <X className="tw:size-4" />
            </Button>
          )}
        </div>
      )
    },
    [isViewMode, removeValue],
  )

  const handleAddMaterial = React.useCallback(() => {
    // Use storedValues (real data) not fieldValues (includes placeholder)
    // to avoid writing at index 1 when the field is empty.
    openPickerForIndex(storedValues?.length || 0)
  }, [openPickerForIndex, storedValues?.length])

  const bulkAction =
    useIndividualSelectors && !isViewMode ? (
      <button
        type="button"
        onClick={handleBulkClick}
        className="tw:flex tw:items-center tw:gap-1 tw:px-2 tw:py-1 tw:rounded-md tw:text-sm tw:font-medium tw:text-foreground tw:bg-transparent tw:border-none tw:cursor-pointer hover:tw:bg-background/60 tw:transition-colors"
      >
        <ListChecks className="tw:size-4" />
        {Localization.translator.bulk}
      </button>
    ) : undefined

  return (
    <PermitBaseFormField
      typeField={typeField}
      fieldValues={fieldValues}
      isViewMode={isViewMode}
      parentInstanceId={parentInstanceId}
      parentValueIndex={parentValueIndex}
      store={store}
      fieldsStore={fieldsStore}
      componentRenderer={renderComponent}
      viewModeRenderer={renderViewMode}
      isViewModeContentFullWidth={showGroupTitle}
      isHeaderForced={hasAssociatedFields}
      headerActions={bulkAction}
      titleRenderer={useIndividualSelectors ? renderTitle : undefined}
      onAddFieldClick={useIndividualSelectors ? handleAddMaterial : undefined}
      onRemoveField={removeValue}
    />
  )
}

export default inject('state')(observer(PermitFormMaterialResourceField))
